/**
 * 参考资料链接列表组件
 *
 * 这个组件用于显示聊天中引用的参考资料列表，包括：
 * - 可折叠的参考资料列表
 * - 显示参考资料数量和标题
 * - 提供外部链接跳转功能
 * - 支持展开/收起动画效果
 */

import { ChevronRight } from 'lucide-react'
import { useState } from 'react'
import './SourceLinkList.less'

/**
 * 参考资料项类型定义
 */
type SourceItem = {
  /** 资料类型 */
  type: string
  /** 资料标题 */
  title: string
  /** 资料链接地址 */
  url: string
  /** 资料详细信息 */
  detail: string[]
  /** 文件ID */
  file_id: string
  /** 知识库名称 */
  knowledge_name: string
  /** 工具调用信息 */
  tool_calls: any[]
}

/**
 * 组件属性类型定义
 */
type Props = {
  /** 参考资料数据数组 */
  data: SourceItem[]
}

/**
 * 参考资料链接列表组件
 *
 * @param data - 参考资料数据数组
 * @returns JSX 元素
 */
export default function SourceLinkList({ data }: Props) {
  // 展开/收起状态
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="mb-[20px] w-full overflow-hidden rounded-[10px] bg-[#F5F7FB] transition-all">
      {/* 头部区域 - 显示参考资料数量和展开/收起按钮 */}
      <div
        className="flex h-[46px] cursor-pointer items-center justify-between px-3"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-1">
          {/* 成功图标 - 表示已成功读取资料 */}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_1927_11673)">
              <circle cx="8" cy="8" r="7" stroke="#38D0AF" />
              <path
                d="M10.9678 6.81055L7.81129 9.96703L6.00049 8.15624"
                stroke="#38D0AF"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_1927_11673">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>
          {/* 参考资料数量文本 */}
          <span
            className="whitespace-nowrap text-[14px] font-normal leading-[22px] text-[#000]"
            style={{ fontWeight: 400 }}
          >
            已阅读了{data.length}个资料作为参考
          </span>
        </div>
        {/* 展开/收起箭头图标 */}
        <ChevronRight
          className={`h-4 w-4 transform text-[#000] transition-transform duration-300 ${expanded ? 'rotate-90' : ''}`}
        />
      </div>

      {/* 参考资料列表区域 - 可折叠显示 */}
      <div
        className={`flex flex-col px-3 transition-all duration-300 ${
          expanded ? 'mb-3 mt-[4px] max-h-[500px] opacity-100' : 'max-h-0 py-0 opacity-0'
        } caseOverFlowY`}
        // } overflow-hidden`}
      >
        {/* 遍历显示每个参考资料项 */}
        {data.map((item, idx) => (
          <div key={idx} className="source-link-list-item text-[14px] font-normal leading-[22px] text-[#1968FF]">
            {/* 序号 */}
            <span>{idx + 1}.</span>
            {/* 参考资料链接 */}
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-3 overflow-hidden text-ellipsis whitespace-nowrap "
              style={{ fontWeight: 400 }}
            >
              {item.title}
            </a>
          </div>
        ))}
      </div>
    </div>
  )
}
